/**
 * Format a date string into a readable format
 * @param {string|Date} date
 * @returns {string}
 */
export const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Format file size in bytes to human-readable string
 * @param {number} bytes
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Retrieve stored user from localStorage
 * @returns {object|null}
 */
export const getStoredUser = () => {
  try {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

/**
 * Retrieve stored token from localStorage
 * @returns {string|null}
 */
export const getStoredToken = () => localStorage.getItem("token");

const COUNTRY_NAME_TO_DIAL_CODE = {
  "afghanistan": 93,
  "albania": 355,
  "algeria": 213,
  "andorra": 376,
  "angola": 244,
  "argentina": 54,
  "armenia": 374,
  "australia": 61,
  "austria": 43,
  "azerbaijan": 994,
  "bahamas": 1,
  "bahrain": 973,
  "bangladesh": 880,
  "barbados": 1,
  "belarus": 375,
  "belgium": 32,
  "belize": 501,
  "benin": 229,
  "bhutan": 975,
  "bolivia": 591,
  "bosnia and herzegovina": 387,
  "botswana": 267,
  "brazil": 55,
  "brunei": 673,
  "bulgaria": 359,
  "burkina faso": 226,
  "burundi": 257,
  "cabo verde": 238,
  "cambodia": 855,
  "cameroon": 237,
  "canada": 1,
  "central african republic": 236,
  "chad": 235,
  "chile": 56,
  "china": 86,
  "colombia": 57,
  "comoros": 269,
  "congo": 242,
  "costa rica": 506,
  "croatia": 385,
  "cuba": 53,
  "cyprus": 357,
  "czech republic": 420,
  "czechia": 420,
  "denmark": 45,
  "djibouti": 253,
  "dominica": 1,
  "dominican republic": 1,
  "ecuador": 593,
  "egypt": 20,
  "el salvador": 503,
  "equatorial guinea": 240,
  "eritrea": 291,
  "estonia": 372,
  "eswatini": 268,
  "ethiopia": 251,
  "fiji": 679,
  "finland": 358,
  "france": 33,
  "gabon": 241,
  "gambia": 220,
  "georgia": 995,
  "germany": 49,
  "ghana": 233,
  "greece": 30,
  "grenada": 1,
  "guatemala": 502,
  "guinea": 224,
  "guinea-bissau": 245,
  "guyana": 592,
  "haiti": 509,
  "honduras": 504,
  "hungary": 36,
  "iceland": 354,
  "india": 91,
  "indonesia": 62,
  "iran": 98,
  "iraq": 964,
  "ireland": 353,
  "israel": 972,
  "italy": 39,
  "jamaica": 1,
  "japan": 81,
  "jordan": 962,
  "kazakhstan": 7,
  "kenya": 254,
  "kiribati": 686,
  "kuwait": 965,
  "kyrgyzstan": 996,
  "laos": 856,
  "latvia": 371,
  "lebanon": 961,
  "lesotho": 266,
  "liberia": 231,
  "libya": 218,
  "liechtenstein": 423,
  "lithuania": 370,
  "luxembourg": 352,
  "madagascar": 261,
  "malawi": 265,
  "malaysia": 60,
  "maldives": 960,
  "mali": 223,
  "malta": 356,
  "marshall islands": 692,
  "mauritania": 222,
  "mauritius": 230,
  "mexico": 52,
  "micronesia": 691,
  "moldova": 373,
  "monaco": 377,
  "mongolia": 976,
  "montenegro": 382,
  "morocco": 212,
  "mozambique": 258,
  "myanmar": 95,
  "namibia": 264,
  "nauru": 674,
  "nepal": 977,
  "netherlands": 31,
  "new zealand": 64,
  "nicaragua": 505,
  "niger": 227,
  "nigeria": 234,
  "north korea": 850,
  "north macedonia": 389,
  "norway": 47,
  "oman": 968,
  "pakistan": 92,
  "palau": 680,
  "palestine": 970,
  "panama": 507,
  "papua new guinea": 675,
  "paraguay": 595,
  "peru": 51,
  "philippines": 63,
  "poland": 48,
  "portugal": 351,
  "qatar": 974,
  "romania": 40,
  "russia": 7,
  "rwanda": 250,
  "saint kitts and nevis": 1,
  "saint lucia": 1,
  "saint vincent and the grenadines": 1,
  "samoa": 685,
  "san marino": 378,
  "sao tome and principe": 239,
  "saudi arabia": 966,
  "senegal": 221,
  "serbia": 381,
  "seychelles": 248,
  "sierra leone": 232,
  "singapore": 65,
  "slovakia": 421,
  "slovenia": 386,
  "solomon islands": 677,
  "somalia": 252,
  "south africa": 27,
  "south korea": 82,
  "south sudan": 211,
  "spain": 34,
  "sri lanka": 94,
  "sudan": 249,
  "suriname": 597,
  "sweden": 46,
  "switzerland": 41,
  "syria": 963,
  "taiwan": 886,
  "tajikistan": 992,
  "tanzania": 255,
  "thailand": 66,
  "timor-leste": 670,
  "togo": 228,
  "tonga": 676,
  "trinidad and tobago": 1,
  "tunisia": 216,
  "turkey": 90,
  "turkiye": 90,
  "türkiye": 90,
  "turkmenistan": 993,
  "tuvalu": 688,
  "uganda": 256,
  "ukraine": 380,
  "united arab emirates": 971,
  "united kingdom": 44,
  "united states": 1,
  "uruguay": 598,
  "uzbekistan": 998,
  "vanuatu": 678,
  "vatican city": 379,
  "venezuela": 58,
  "vietnam": 84,
  "yemen": 967,
  "zambia": 260,
  "zimbabwe": 263,
};

export const getCountryCodeFromCountryName = (country) => {
  if (!country) return null;
  const normalized = String(country).trim().toLowerCase().replace(/\s+/g, " ");
  return COUNTRY_NAME_TO_DIAL_CODE[normalized] ?? null;
};

export const resolveUserCountryCode = (user, defaultCode = null) => {
  const direct = Number(user?.CountryCode ?? user?.countryCode);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const mapped = getCountryCodeFromCountryName(user?.Country ?? user?.country);
  if (Number.isFinite(mapped) && mapped > 0) return mapped;

  return defaultCode;
};

const pad2 = (n) => String(n).padStart(2, "0");

export const toFolderSegment = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "upload";

  const cleaned = raw
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  return cleaned || "upload";
};

export const normalizeUploadFolderName = (folderName) => {
  if (folderName == null) return null;
  return toFolderSegment(folderName);
};

export const buildPPFolderName = ({ baseName, date = new Date() } = {}) => {
  const d = date instanceof Date ? date : new Date(date);
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());

  const datePart = `${yyyy}${mm}${dd}`;
  const namePart = toFolderSegment(baseName || "Upload");

  return `${datePart}_${namePart}`;
};
